import {
  FileText,
  Send,
  Sparkles,
  Upload,
  UserRound,
  Waypoints,
} from 'lucide-react';
import {
  inputClassName,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

function QueueView({
  currentUser,
  queueForm,
  selectedFile,
  isSubmittingQueue,
  queueStatus,
  lastQueue,
  lastProcessingJob,
  onSubmit,
  onMessageChange,
  onScreenChange,
  onFileChange,
}) {
  const statusTone =
    queueStatus.type === 'warning'
      ? 'warning'
      : queueStatus.type === 'loading'
        ? 'loading'
        : queueStatus.type || 'neutral';

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Queue"
        title="Submit a new request"
        lede="Prepare the request payload, attach an optional file, and send it to the existing queue pipeline without changing any backend behavior."
        bearyMessage={queueStatus.type === 'loading' ? 'Beary is sending this request through the queue.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={statusTone}>{queueStatus.message || 'Ready to submit'}</StatusPill>}
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <MetricCard label="Request type" value={queueForm.type || 'Queue'} icon={Sparkles} />
        <MetricCard label="Source" value={queueForm.source || 'Manual'} icon={Waypoints} />
        <MetricCard
          label="Selected file"
          value={selectedFile ? 'Attached' : 'Optional'}
          note={selectedFile?.name || 'No file selected yet'}
          icon={FileText}
        />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <SectionCard>
          <SectionTitle
            title="Request payload"
            copy="The request shape and handlers stay unchanged. This pass only moves the submission surface onto the new card system."
          />

          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="queue-message">
                Message
              </label>
              <textarea
                id="queue-message"
                name="message"
                rows="6"
                value={queueForm.message}
                onChange={(event) => onMessageChange(event.target.value)}
                disabled={isSubmittingQueue}
                placeholder="Describe what should happen with this queue request."
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="queue-screen">
                Screen
              </label>
              <input
                id="queue-screen"
                name="screen"
                value={queueForm.screen}
                onChange={(event) => onScreenChange(event.target.value)}
                disabled={isSubmittingQueue}
                placeholder="tasks"
                className={inputClassName}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-app-ink" htmlFor="queue-file">
                Select file
              </label>
              <label className="flex cursor-pointer flex-col gap-3 rounded-[22px] border border-dashed border-app-border bg-app-surface-alt/60 px-5 py-5 transition hover:border-app-accent/30 hover:bg-app-surface-alt">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                    <Upload className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-app-ink">
                      {selectedFile?.name || 'Choose a file'}
                    </span>
                    <span className="block text-sm text-app-muted">
                      Optional upload for document processing flows.
                    </span>
                  </span>
                </div>
                <input
                  id="queue-file"
                  name="file"
                  type="file"
                  onChange={(event) => onFileChange(event.target.files?.[0] || null)}
                  disabled={isSubmittingQueue}
                  className="sr-only"
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
                type="submit"
                disabled={isSubmittingQueue}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                <span>{isSubmittingQueue ? 'Uploading...' : 'Upload request'}</span>
              </button>
            </div>
          </form>
        </SectionCard>

        <aside className="space-y-6">
          <SectionCard className="bg-app-shell">
            <SectionTitle
              title="Session details"
              copy="Keep the visible submission metadata near the form."
            />
            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-app-border/70 bg-app-surface/85 p-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-app-surface-alt text-app-muted">
                    <UserRound className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Signed in
                    </p>
                    <p className="mt-1 text-sm font-medium text-app-ink">
                      {currentUser?.email || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-app-border/70 bg-app-surface/85 p-4">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                  Firebase UID
                </p>
                <p className="mt-2 break-all text-sm font-medium text-app-ink">
                  {currentUser?.firebaseUid || currentUser?.userId || 'Unknown'}
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard>
            <SectionTitle
              title="Latest submission"
              copy="Queue and processing metadata remain unchanged."
            />

            {lastQueue ? (
              <div className="mt-5 space-y-4">
                <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                  <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                    Queue ID
                  </p>
                  <p className="mt-2 break-all text-sm font-medium text-app-ink">
                    {lastQueue._id || 'Unavailable'}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Status
                    </p>
                    <p className="mt-2 text-sm font-medium text-app-ink">
                      {lastQueue.status || 'Unknown'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Processing job
                    </p>
                    <p className="mt-2 break-all text-sm font-medium text-app-ink">
                      {lastProcessingJob?._id || 'Not started'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Created at
                    </p>
                    <p className="mt-2 text-sm font-medium text-app-ink">
                      {lastQueue.createdAt || 'Unavailable'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-app-border/70 bg-app-surface-alt/70 p-4">
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">
                      Blob URL
                    </p>
                    <p className="mt-2 break-all text-sm font-medium text-app-ink">
                      {lastQueue.storage?.blobUrl || 'Unavailable'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="mt-5 text-sm leading-6 text-app-muted">
                No queue submission has been recorded in this session yet.
              </p>
            )}
          </SectionCard>
        </aside>
      </div>
    </section>
  );
}

export default QueueView;
